import { apiFetch } from "../utils/apiFetch";
import React, { useEffect, useRef, useState } from "react";
import { FiLoader } from "react-icons/fi";

const NewClassPopup = ({ setNewClassPopup, workspace, setClasses, modules = [], initialModuleId = "", setupSteps, requireModule = false, onCreated }) => {
  const [saving, setSaving] = useState(false), [error, setError] = useState("");
  const savePending = useRef(false), dialogRef = useRef(null);
  useEffect(() => {
    const dialog = dialogRef.current, focused = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); focused?.focus(); };
  }, []);
  const [moduleId, setModuleId] = useState(initialModuleId === "general" ? "" : initialModuleId);
  const [classTitle, setClassTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [classType, setClassType] = useState("prerecorded");
  const [classTime, setClassTime] = useState("");
  const uploadRequestRef = useRef(null);

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileType = getFileType(file);
    if (fileType === "video" && classType !== "prerecorded") {
      // alert("please select a video");
      // e.target.value = "";
      // setSelectedFile(null);
      setClassType("prerecorded");
      return;
    }

    if (fileType === "file" && classType !== "resource") {
      // alert("please select a file");
      // e.target.value = "";
      // setSelectedFile(null);
      setClassType("resource");
      return;
    }

    setSelectedFile(file);
  };

  const getFileType = (file) => {
    if (file.type.startsWith("video/")) return "video";
    return "file";
  };

  const postClassToDatabase = async (payload) => {
    if (savePending.current) return;
    if (!classTitle.trim() || !description.trim() || !classType || (requireModule && !moduleId)) {
      setError("Complete the class title, description and module.");
      return;
    }
    savePending.current = true; setSaving(true); setError("");
    try {
      const postToDBRes = await apiFetch(`${process.env.API_URL}/api/newclass`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",

        },
        body: JSON.stringify({ ...payload, moduleId: moduleId || null }),
      });

      const DBjson = await postToDBRes.json();

      if (!postToDBRes.ok) {
        throw new Error(postToDBRes.status + ": " + DBjson.message);
      }

      setClasses((prev) => [...prev, DBjson.cls]);
      if (onCreated) onCreated(DBjson.cls);
      else setNewClassPopup(null);
    } catch (err) {
      setError(err.message);
    } finally {
      savePending.current = false; setSaving(false); setIsUploading(false);
    }
  };

  const uploadClass = async () => {
    if (isUploading || saving) return;
    if (!classTitle || !selectedFile || !description || !classType) {
      alert("All fields required");
      return;
    }

    const fileType = getFileType(selectedFile);

    setLoadingSpinner(true);
    setUploadProgress(0);
    setIsUploading(true);

    try {
      const sigRes = await apiFetch(
        `${process.env.API_URL}/api/r2/get-signed-upload-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",

          },
          body: JSON.stringify({
            workspaceId: workspace._id,
            fileName: selectedFile.name,
            fileType: selectedFile.type,
            folder: fileType === "video" ? "classes/videos" : "classes/files",
          }),
        },
      );

      if (!sigRes.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { uploadUrl, key, publicUrl } = await sigRes.json();

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        uploadRequestRef.current = xhr;

        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader(
          "Content-Type",
          selectedFile.type || "application/octet-stream",
        );

        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;

          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        };

        xhr.onload = () => {
          uploadRequestRef.current = null;
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          uploadRequestRef.current = null;
          reject(new Error("Network error during upload"));
        };

        xhr.onabort = () => {
          uploadRequestRef.current = null;
          reject(new Error("Upload cancelled"));
        };

        xhr.send(selectedFile);
      });

      const payload = {
        workspaceId: workspace._id,
        title: classTitle,
        description,
        type: classType,
        video:
          classType === "prerecorded"
            ? {
                url: publicUrl,
                key,
                name: selectedFile.name,
                mime: selectedFile.type,
                size: selectedFile.size,
              }
            : {},
        attachments:
          classType === "resource"
            ? [
                {
                  url: publicUrl,
                  key,
                  name: selectedFile.name,
                  mime: selectedFile.type,
                  size: selectedFile.size,
                },
              ]
            : [],
        status: null,
      };

      await postClassToDatabase(payload);

      // const postToDBRes = await apiFetch(`${process.env.API_URL}/api/newclass`, {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //
      //   },
      //   body: JSON.stringify(payload),
      // });

      // if (!postToDBRes.ok) {
      //   throw new Error(postToDBRes.status + ": " + postToDBRes.statusText);
      // }

      // const DBjson = await postToDBRes.json();
      // console.log(DBjson);
      // setClasses((prev) => [...prev, DBjson.cls]);

      // setLoadingSpinner(false);
      // setUploadProgress(null);
      // setIsUploading(false);
      // setNewClassPopup(null);
    } catch (err) {
      setLoadingSpinner(false);
      setUploadProgress(null);
      setIsUploading(false);
      alert(err.message);
    }
  };

  const cancelUpload = () => {
    uploadRequestRef.current?.abort();
  };

  return (
    <dialog ref={dialogRef} aria-label="Add class" onCancel={event => { event.preventDefault(); if (!saving && !isUploading) setNewClassPopup(false); }}
        className="m-auto w-[calc(100%-2rem)] max-w-lg max-h-[85dvh] space-y-5 overflow-y-auto rounded-lg border border-gray-300 bg-white p-6 text-sm text-gray-900 shadow-xl backdrop:bg-black/40 [&_input]:max-w-full [&_input]:rounded-md [&_input]:border-gray-300 [&_input]:p-2 [&_select]:rounded-md [&_select]:p-2 [&_label]:block"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Add class</h2>
          <button type="button" disabled={saving || isUploading} onClick={() => setNewClassPopup(false)} className="text-sm text-gray-500 hover:text-gray-900 disabled:opacity-50">Close</button>
        </div>
        {setupSteps}
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div>
          <label>
            Class title:{" "}
            <input
              type="text"
              className="border"
              value={classTitle}
              onChange={(e) => setClassTitle(e.target.value)}
            ></input>
          </label>
        </div>
        <label>Module
          <select aria-label="Module" value={moduleId} onChange={event => setModuleId(event.target.value)} className="mt-1 w-full border" disabled={isUploading || saving || requireModule}>
            {!requireModule && <option value="">General</option>}
            {modules.map(module => <option key={module._id} value={module._id}>{module.title}</option>)}
          </select>
        </label>
        <div>
          <label>
            Class description:{" "}
            <textarea
              className="border"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></textarea>
          </label>
        </div>
        <div>
          <label>
            Class type:
            <select
              value={classType}
              onChange={(e) => {
                setSelectedFile(null);
                setClassType(e.target.value);
              }}
              className="border"
            >
              <option value={"prerecorded"}>Upload existing video</option>
              <option value={"live"}>Schedule online class</option>
              <option value={"resource"}>Files</option>
            </select>
          </label>
        </div>

        {classType === "prerecorded" && (
          <input
            key={classType}
            type="file"
            accept="video/*"
            onChange={onSelectFile}
          />
        )}

        {classType === "resource" && (
          <input
            key={classType}
            type="file"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
            onChange={onSelectFile}
          />
        )}

        {classType === "live" && (
          <label>
            Class time (IST):{" "}
            <input
              className="border"
              type="datetime-local"
              value={classTime}
              onChange={(e) => setClassTime(e.target.value)}
            />
          </label>
        )}
        <button
          disabled={isUploading || saving} aria-busy={isUploading || saving}
          onClick={() => {
            if (classType !== "live") {
              uploadClass();
              return;
            }
            if (!classTime) {
              alert("Please pick a class time (IST).");
              return;
            }
            const payload = {
              workspaceId: workspace._id,
              title: classTitle,
              description,
              type: classType,
              video: {},
              attachments: [],
              scheduledAt: `${classTime}+05:30`,
              status: "scheduled",
            };
            postClassToDatabase(payload);
          }}
          className="flex min-h-10 items-center gap-2 rounded-md bg-orange-700 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-800 disabled:opacity-50"
        >
          {/* {loadingSpinner && (
            <svg
              className="mr-3 size-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-25"
              />
              <path
                d="M22 12a10 10 0 0 1-10 10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-75"
              />
            </svg>
          )} */}
          {isUploading && (
            <span className="relative flex size-8 items-center justify-center">
              <svg className="size-full -rotate-90" viewBox="0 0 100 100">
                <circle
                  className="fill-none stroke-gray-200"
                  cx="50"
                  cy="50"
                  r="42"
                  strokeWidth="8"
                />

                <circle
                  className="fill-none stroke-green-600 transition-all duration-200"
                  cx="50"
                  cy="50"
                  r="42"
                  strokeWidth="8"
                  strokeLinecap="round"
                  pathLength="100"
                  strokeDasharray="100"
                  strokeDashoffset={
                    100 - Math.min(100, Math.max(0, uploadProgress))
                  }
                />
              </svg>

              <span className="absolute text-xs font-semibold text-green-700">
                {Math.round(Math.min(100, Math.max(0, uploadProgress)))}%
              </span>
            </span>
          )}
          {saving && <FiLoader className="animate-spin" />}{classType !== "live" ? "Upload" : "Schedule"}
        </button>

        {isUploading && (
          <button
            onClick={cancelUpload}
            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Cancel Upload
          </button>
        )}
    </dialog>
  );
};

export default NewClassPopup;
